import { useState } from 'react';
import { motion } from 'framer-motion';
import { ClipboardList, CheckCircle2, XCircle, Clock, Users, Check, X } from 'lucide-react';
import useFetch from '../../hooks/useFetch';
import { patch, post } from '../../api/client';
import { useEvents } from '../../context/EventContext';
import { useToast } from '../../context/ToastContext';
import EventPicker from '../../components/EventPicker';
import {
  Stat,
  Button,
  Table,
  Badge,
  StatusBadge,
  Loader,
  EmptyState,
  SectionTitle,
  Tabs,
  Pagination,
  SearchInput,
  Modal,
  ConfirmModal,
  label,
} from '../../components/UI';
import { fmtDateTime, fmtRelative } from '../../utils/format';

export default function Registrations() {
  const { eventId, events } = useEvents();
  const toast = useToast();

  const [status, setStatus] = useState('pending');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [detail, setDetail] = useState(null);
  const [bulk, setBulk] = useState(null);

  const { data: rows, meta, loading, reload } = useFetch(
    '/registrations',
    { page, limit: 25, ...(eventId ? { event: eventId } : {}), ...(status ? { status } : {}), ...(search ? { search } : {}) },
    { withMeta: true },
  );

  if (!events.length) {
    return <EmptyState message="No events available" hint="Open registration becomes available once you have an event." icon={ClipboardList} />;
  }

  const counts = meta?.byStatus || {};

  const review = async (id, next) => {
    try {
      await patch(`/registrations/${id}/review`, { status: next });
      toast.success(`Registration ${next}`);
      setDetail(null);
      reload();
    } catch (err) { toast.error(err.message); }
  };

  const runBulk = async () => {
    try {
      const res = await post('/registrations/bulk-review', { ids: selected, status: bulk });
      toast.success(res.message);
      setSelected([]);
      setBulk(null);
      reload();
    } catch (err) { toast.error(err.message); }
  };

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const allSelected = rows?.length > 0 && selected.length === rows.length;

  return (
    <>
      <SectionTitle
        title="Registrations"
        subtitle="Requests received through your public event registration form"
        action={<EventPicker />}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(195px, 1fr))', gap: 13, marginBottom: 18 }}>
        <Stat label="Pending Review" value={counts.pending || 0} icon={Clock} tone="warning" />
        <Stat label="Approved" value={counts.approved || 0} icon={CheckCircle2} tone="success" />
        <Stat label="Waitlisted" value={counts.waitlisted || 0} icon={Users} tone="info" />
        <Stat label="Rejected" value={counts.rejected || 0} icon={XCircle} tone="danger" />
      </div>

      <Tabs
        active={status}
        onChange={(k) => { setStatus(k); setPage(1); setSelected([]); }}
        tabs={[
          { key: 'pending', label: 'Pending', count: counts.pending || 0 },
          { key: 'approved', label: 'Approved', count: counts.approved || 0 },
          { key: 'waitlisted', label: 'Waitlisted', count: counts.waitlisted || 0 },
          { key: 'rejected', label: 'Rejected', count: counts.rejected || 0 },
          { key: '', label: 'All' },
        ]}
      />

      <div style={{ display: 'flex', gap: 11, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <SearchInput value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search by name, email or company…" style={{ flex: 1, minWidth: 220 }} />
        {selected.length > 0 && (
          <motion.div initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} style={{ display: 'flex', gap: 8 }}>
            <Badge tone="gold">{selected.length} selected</Badge>
            <Button size="sm" icon={Check} onClick={() => setBulk('approved')}>Approve</Button>
            <Button size="sm" variant="danger" icon={X} onClick={() => setBulk('rejected')}>Reject</Button>
          </motion.div>
        )}
      </div>

      {loading ? <Loader label="Loading registrations" /> : !rows?.length ? (
        <EmptyState
          message={`No ${status || ''} registrations`}
          hint="Share the public registration link from the event's Registration tab to start collecting sign-ups."
          icon={ClipboardList}
        />
      ) : (
        <>
          <Table
            rows={rows}
            onRowClick={setDetail}
            columns={[
              { key: 'select', title: (
                <input type="checkbox" checked={allSelected}
                  onChange={() => setSelected(allSelected ? [] : rows.map((r) => r._id))}
                  style={{ width: 14, height: 14, accentColor: 'var(--gold-400)' }} />
              ), width: 40, render: (r) => (
                <input type="checkbox" checked={selected.includes(r._id)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggle(r._id)}
                  style={{ width: 14, height: 14, accentColor: 'var(--gold-400)' }} />
              )},
              { key: 'name', title: 'Applicant', render: (r) => (
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{r.name}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-lo)' }}>{r.email}</div>
                </div>
              )},
              { key: 'company', title: 'Company', render: (r) => (
                <div>
                  <div style={{ fontSize: 12.5 }}>{r.company || '—'}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-lo)' }}>{r.designation || ''}</div>
                </div>
              )},
              { key: 'mobile', title: 'Mobile', render: (r) => r.mobile },
              { key: 'status', title: 'Status', render: (r) => <StatusBadge status={r.status} /> },
              { key: 'createdAt', title: 'Received', render: (r) => (
                <span style={{ fontSize: 12, color: 'var(--text-lo)' }}>{fmtRelative(r.createdAt)}</span>
              )},
              { key: 'actions', title: '', align: 'right', render: (r) => (
                r.status === 'pending' || r.status === 'waitlisted' ? (
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    <button onClick={(e) => { e.stopPropagation(); review(r._id, 'approved'); }}
                      title="Approve" style={{ color: 'var(--ok)', padding: 5, display: 'flex' }}>
                      <CheckCircle2 size={16} />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); review(r._id, 'rejected'); }}
                      title="Reject" style={{ color: 'var(--danger)', padding: 5, display: 'flex' }}>
                      <XCircle size={16} />
                    </button>
                  </div>
                ) : null
              )},
            ]}
          />
          <Pagination meta={meta} onChange={setPage} />
        </>
      )}

      {/* detail */}
      <Modal
        open={Boolean(detail)} onClose={() => setDetail(null)}
        title={detail?.name} subtitle={detail?.email}
        footer={detail?.status === 'pending' && (
          <>
            <Button variant="danger" onClick={() => review(detail._id, 'rejected')}>Reject</Button>
            <Button onClick={() => review(detail._id, 'approved')}>Approve & create account</Button>
          </>
        )}
      >
        {detail && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
            <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
              <StatusBadge status={detail.status} />
              <Badge tone="neutral">{label(detail.source)}</Badge>
              {detail.createdUser && <Badge tone="success">Account created</Badge>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
              <Detail label="Mobile" value={detail.mobile} />
              <Detail label="Company" value={detail.company || '—'} />
              <Detail label="Designation" value={detail.designation || '—'} />
              <Detail label="Received" value={fmtDateTime(detail.createdAt)} />
            </div>

            {detail.responses && Object.keys(detail.responses).length > 0 && (
              <div>
                <p style={{ fontSize: 11, color: 'var(--text-lo)', textTransform: 'uppercase', letterSpacing: 0.9, fontWeight: 600, marginBottom: 10 }}>
                  Form responses
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {Object.entries(detail.responses).map(([k, v]) => (
                    <div key={k} style={{
                      display: 'flex', justifyContent: 'space-between', gap: 14,
                      padding: '10px 13px', background: 'var(--ink-800)',
                      border: '1px solid var(--border)', borderRadius: 'var(--r)',
                    }}>
                      <span style={{ fontSize: 12.5, color: 'var(--text-mid)', textTransform: 'capitalize' }}>{k}</span>
                      <span style={{ fontSize: 12.5, fontWeight: 500 }}>{String(v) || '—'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {detail.rejectionReason && (
              <div style={{
                padding: 13, background: 'var(--danger-dim)',
                border: '1px solid rgba(248,113,113,0.25)', borderRadius: 'var(--r)',
              }}>
                <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--danger)', marginBottom: 4 }}>Rejection reason</p>
                <p style={{ fontSize: 12.5, color: 'var(--text-mid)' }}>{detail.rejectionReason}</p>
              </div>
            )}
          </div>
        )}
      </Modal>

      <ConfirmModal
        open={Boolean(bulk)} onClose={() => setBulk(null)} onConfirm={runBulk}
        danger={bulk === 'rejected'}
        title={`${bulk === 'approved' ? 'Approve' : 'Reject'} ${selected.length} registration${selected.length === 1 ? '' : 's'}?`}
        confirmLabel={bulk === 'approved' ? 'Approve all' : 'Reject all'}
        message={bulk === 'approved'
          ? 'A platform account will be created for each applicant and they will be welcomed by email and WhatsApp.'
          : 'These applicants will be marked rejected. They are not notified automatically.'}
      />
    </>
  );
}

function Detail({ label: text, value }) {
  return (
    <div>
      <p style={{ fontSize: 11, color: 'var(--text-lo)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>{text}</p>
      <p style={{ fontSize: 13.5, marginTop: 4 }}>{value}</p>
    </div>
  );
}
